import differenceInCalendarYears from 'date-fns/differenceInCalendarYears';
import { policyType } from '@ourbranch/lookups';
import { checkIsFarmEnvironment } from 'common/helpers/environment-helpers';

const isFarmEnvironment = checkIsFarmEnvironment();

export const getInitialValues = (prefillData, user, requestedPolicyType) => {
  const initialValues = {
    firstName: undefined,
    lastName: undefined,
    address: undefined,
    driversLicenseNumber: '',
    driversLicenseState: '',
    state: undefined,
    zip: undefined,
    city: undefined,
    unit: '',
    noUnitNumber: false,
    dateOfBirth: '',
    email: undefined,
    phone: undefined,
    leadSource: undefined,
    affinity: user?.affinityCode,
    rep: user?.username,
    VIN: '',
    priorAddress: {
      address: '',
      address2: '',
      state: '',
      zip: '',
      city: ''
    },
    home: {
      sqFt: undefined,
      ownershipStatus: undefined,
      homeQuality: undefined,
      typeOfHome: undefined,
      constructionType: undefined,
      exteriorWallType: undefined,
      garageType: undefined,
      roofShape: undefined,
      roofDeck: undefined,
      roofCover: undefined,
      roofDeckAttachment: undefined,
      roofToWallConnection: undefined,
      openingProtection: undefined,
      roofSWR: undefined,
      yearBuilt: undefined,
      numFireplaces: undefined,
      numWoodBurningStoves: undefined,
      numOccupants: undefined,
      numMortgages: undefined,
      numFullBathrooms: undefined,
      numHalfBathrooms: undefined,
      numStories: undefined,
      garageCarCapacity: undefined,
      numFamiliesInDwelling: undefined,
      roofYear: undefined,
      roofType: undefined,
      roofOriginal: undefined,
      purchaseDate: undefined,
      basementSqFt: undefined,
      basementType: undefined,
      latitude: undefined,
      longitude: undefined,
      fireHydrantWithin1000ft: undefined,
      fpc: undefined,
      fpcFromLocation: undefined,
      wildfireHazardScore: undefined,
      wildfireGallagherScore: undefined,
      dtoName: undefined,
      dtbowName: undefined,
      dtbowLabel: undefined,
      stormShutters: undefined
    },
    condo: {
      yearBuilt: undefined,
      purchaseDate: undefined,
      monthsRentedOut: undefined,
      floor: undefined,
      typeOfEntry: 'I',
      hasBasement: false,
      buildingHasMoreThanFiveUnits: false,
      condoQuality: undefined,
      constructionType: undefined,
      numHalfBathrooms: undefined,
      numFullBathrooms: undefined,
      roofShape: undefined,
      sqFt: undefined,
      latitude: undefined,
      longitude: undefined
    },

    // Farm - New farmLocations array structure
    farmLocations: [
      {
        description: undefined,
        numberOfAcres: undefined,
        address: {
          address: undefined,
          address2: undefined,
          city: undefined,
          state: undefined,
          zip: undefined,
          county: undefined,
          country: undefined
        },
        legalDescription: {
          format: undefined,
          section: undefined,
          township: undefined,
          townshipCoordinates: undefined,
          range: undefined,
          rangeCoordinates: undefined,
          lot: undefined,
          block: undefined
        },
        risks: [
          {
            type: undefined,
            description: undefined,
            useApplicantAddress: true,
            address: {
              address: undefined,
              address2: undefined,
              city: undefined,
              state: undefined,
              zip: undefined,
              county: undefined,
              country: undefined
            },
            building: {
              yearBuilt: undefined,
              purchaseDate: undefined,
              typeOfHome: undefined,
              ownershipStatus: undefined,
              numFamiliesInDwelling: undefined,
              homeQuality: undefined,
              sqFt: undefined,
              constructionType: undefined,
              numSolidFuelBurningAppliances: undefined,
              primaryHeatingSource: undefined,
              secondaryHeatingSource: undefined,
              numStories: undefined,
              numHalfBathrooms: undefined,
              numFullBathrooms: undefined,
              numFireplaces: undefined,
              numWoodBurningStoves: undefined,
              basementType: undefined,
              basementSqFt: undefined,
              exteriorWallType: undefined,
              garageType: undefined,
              garageCarCapacity: undefined,
              attachedStructureSqFt: undefined,
              roofOriginal: undefined,
              roofYear: undefined,
              heatingType: undefined,
              roofType: undefined,
              roofShape: undefined,
              latitude: undefined,
              longitude: undefined,
              tieDownType: undefined,
              enclosedMasonryFoundation: undefined,
              fullSkirtingAroundBase: undefined,
              propertyType: undefined,
              electricalYear: undefined,
              heatingYear: undefined,
              coolingYear: undefined,
              plumbingYear: undefined,
              electricalType: undefined,
              electricalService: undefined,
              constructionStyle: undefined,
              mobileHomeQualityGrade: undefined,
              mobileHomeConfig: undefined
            }
          }
        ]
      }
    ],
    // End farm

    hasAutoInsuranceDetails: true,
    insuranceInPast31Days: false,
    priorCoverageInYears: '',
    policyLimitBIPD: '',
    fcraDisclaimer: false,
    isNewConstruction: false,
    isNewPurchase: false,
    livedAtThisAddressFor1Year: undefined,
    connectedHome: undefined,
    global: undefined,
    signUpForNewMonitoringService: false,
    optIntoMarketingCampaigns: false,
    policyType: isFarmEnvironment ? policyType.Farm : policyType.HABundle,
    isApartment: false,
    overrides: {}
  };

  if (prefillData && Object.keys(prefillData).length > 0) {
    const {
      fname,
      lname,
      firstName,
      lastName,
      salesRep,
      correctedAddress: { address, city, state, zip } = {},
      priorAddress,
      email,
      phone,
      home,
      condo,
      drivers,
      leadSource,
      affinity,
      global,
      policyType: policyTypeFromPrefill,
      autoCoverage,
      // additional properties to support Simply IOA and other agencies who want links in batch files:
      // (these are shorter than full names to reduce the size of the batch file and stay in URL length limits)
      addr, // address
      ci, // city
      st, // state
      zi // zip
    } = prefillData;

    if (prefillData.fname || prefillData.firstName) {
      initialValues.firstName = fname || firstName;
    }
    if (lname || lastName) {
      initialValues.lastName = lname || lastName;
    }
    if (salesRep) {
      initialValues.rep = prefillData.salesRep;
    }
    if (drivers?.length) {
      initialValues.dateOfBirth = drivers[0]?.dateOfBirth;
      initialValues.driversLicenseNumber = drivers[0]?.driversLicenseNumber;
      initialValues.driversLicenseState = drivers[0]?.driversLicenseState;
    }

    if (addr || address) {
      initialValues.address = addr || address;
    }
    if (ci || city) {
      initialValues.city = ci || city;
    }
    if (st || state) {
      initialValues.state = st || state;
    }
    if (zi || zip) {
      initialValues.zip = zi || zip;
    }

    if (priorAddress) {
      initialValues.priorAddress = priorAddress;
    }

    if (autoCoverage?.policyLimitBIPD) {
      initialValues.policyLimitBIPD = autoCoverage?.policyLimitBIPD;
    }

    if (affinity || global?.affinity) {
      initialValues.affinity = affinity || global?.affinity;
    }

    if (home) {
      initialValues.home = home;
    }

    if (condo) {
      initialValues.condo = condo;
    }

    if (phone) {
      initialValues.phone = phone;
    }

    if (email) {
      initialValues.email = email;
    }

    if (leadSource) {
      initialValues.leadSource = leadSource;
    }

    initialValues.insuranceInPast31Days = !!global?.continuousAutoCoverageStartDate;

    initialValues.priorCoverageInYears = global?.continuousAutoCoverageStartDate
      ? Math.min(differenceInCalendarYears(new Date(), Date.parse(global?.continuousAutoCoverageStartDate)), 6)
      : '';

    initialValues.policyType = isFarmEnvironment
      ? policyType.Farm
      : requestedPolicyType || prefillData?.selectedOption || policyTypeFromPrefill || policyType.HABundle;

    initialValues.noUnitNumber = prefillData?.selectedOption?.includes(policyType.Condo) && !prefillData?.hasUnitNumber;
  }

  return initialValues;
};
